/**
 * ============================================================================
 * ⭐⭐⭐ THE RAIL'S SLOT POLICY — PURE, AND **PHYSICALLY** SEPARATE FROM REACT
 * ============================================================================
 *
 * ⛔⛔ THIS FILE EXISTS BECAUSE A DEPLOY ABORTED ON 2026-09-20.
 * `rail-slot-test.cjs` loads its subject through `ts-load.cjs`, which compiles the
 * TypeScript and then `require()`s whatever it imports. The policy lived in
 * `railSlot.ts`, which imports `react` — and react is not resolvable from `tools/`,
 * because the dependency tree lives in `web/`. Stage 2 stopped the deploy with
 * `Cannot find module 'react'`, and was right to.
 *
 * ⛔⛔⛔ AND IT PASSED IN THE AUTHORING CONTAINER, WHICH IS THE REAL LESSON. That
 * machine carried a GLOBALLY INSTALLED react, so `require('react')` resolved from
 * `~/.npm-global` and the test went green in an environment this repo has no reason
 * to reproduce.
 *   ** A HARNESS THAT RESOLVES MODULES DIFFERENTLY FROM THE REAL TREE IS NOT
 *      TESTING THE REAL TREE. **
 * ⚠️ Same family as §4.14: a check that passes because its environment differs from
 * the one that matters.
 *
 * ⭐ SO THE SPLIT IS NOW A FILE BOUNDARY, NOT AN INTENTION. §1AV.178 already claimed
 * "the policy is pure and the React wiring is thin"; this makes that claim something
 * the module loader can verify rather than something a comment asserts.
 *
 * ⛔ **THIS FILE MUST NEVER IMPORT ANYTHING** — not react, not a helper, not a type.
 * `rail-slot-test.cjs` asserts exactly that, so the deploy cannot abort this way a
 * second time.
 *   ** A RULE THE BUILD CAN CHECK IS WORTH TEN COMMENTS ASKING FOR IT. **
 * ============================================================================
 */

/**
 * ---- ⭐⭐ THE RAIL'S ORDER, AS DATA (decision 193) --------------------------------
 *
 * ⛔ PETE'S RULE IS POSITIONAL: *"probably the one on the bottom"*. So the policy has
 * to know which panel is lower, and the only honest source for that is the order the
 * rail renders them in.
 *   ** A RULE ABOUT "THE BOTTOM ONE" NEEDS A LIST, NOT AN INSTINCT. **
 *
 * ⚠️ THIS MUST MATCH THE RENDER ORDER IN `CheckInPanel`. It is the second place that
 * order is declared, and nothing derives it — the same cost the deploy manifest pays,
 * for the same reason: there is no way to read JSX order at runtime without coupling
 * this file to the component it deliberately knows nothing about.
 * ⛔ A code missing from this list sorts LAST (see `lowestOf`), so a new panel that
 * nobody added here becomes the first thing evicted — noticeable, and not silent.
 */
export const RAIL_PANEL_ORDER: readonly string[] = [
    'PAPERWORK', 'LABOUR', 'COMMS', 'ORDERS', 'HISTORY',
] as const;

/** ⭐ Pete: *"One is ideal, two is the max."* */
export const MAX_OPEN_RAIL_PANELS = 2;

/** ⚠️ An unknown code sorts last, so it is evicted first rather than never. */
function rank(code: string): number {
    const i = RAIL_PANEL_ORDER.indexOf(code);
    return i === -1 ? Number.MAX_SAFE_INTEGER : i;
}

/** The positionally LOWEST member of `codes`, ignoring `exclude`. */
function lowestOf(codes: Iterable<string>, exclude: string): string | null {
    let worst: string | null = null;
    for (const c of codes) {
        if (c === exclude) continue;
        if (worst === null || rank(c) > rank(worst)) worst = c;
    }
    return worst;
}

/**
 * ---- ⭐⭐⭐ THE WHOLE POLICY, AS A PURE FUNCTION (decision 193) -------------------
 *
 * ⛔⛔ THIS IS PURE **BECAUSE THE LAST ATTEMPT AT THIS RACED AND WAS REVERTED IN A
 * DAY.** The accordion's logic lived inside effects and could only be reasoned about
 * by imagining React's commit order. Here the decision is a function of its inputs,
 * `rail-slot-test.cjs` runs every case that matters, and the React wiring shrinks to
 * "call this, store the answer".
 *   ** LOGIC YOU CANNOT TEST IS LOGIC YOU ARE GUESSING ABOUT. **
 *
 * ⭐ THE OPENER CAN NEVER BE THE VICTIM — it is passed in as `code` and excluded by
 * construction, not by ordering luck. That single property is what the reverted
 * version could not guarantee.
 *
 * ⚠️ RETURNS THE **SAME SET** WHEN NOTHING CHANGED, so React bails out of the
 * re-render entirely. That behaviour came from `railSlotReducer` and is kept.
 */
export function nextRailSlots(
    prev: ReadonlySet<string>,
    prevEvicted: string | null,
    code: string,
    open: boolean,
): { open: ReadonlySet<string>; evicted: string | null } {
    /* ⚠️ NO CHANGE TO MEMBERSHIP — but a CLOSE still has to clear an eviction aimed at
       this panel, or the name survives and evicts it again the moment it re-opens.
       ⛔ That is not hypothetical: the victim closes itself in response to being
       evicted, and that close is exactly this no-change path. */
    if (open === prev.has(code)) {
        return { open: prev, evicted: !open && prevEvicted === code ? null : prevEvicted };
    }

    /* ⭐ MEMBERSHIP IS STILL `railSlotReducer`'s JOB — one writer for "who is open",
       one writer for "who was pushed out". */
    const next = new Set(railSlotReducer(new Set(prev), code, open));

    if (!open) {
        /* ⭐ A CLOSE NEVER EVICTS. And it clears an eviction pointed at itself — the
           ordinary way a victim leaves. */
        return { open: next, evicted: prevEvicted === code ? null : prevEvicted };
    }

    if (next.size <= MAX_OPEN_RAIL_PANELS) return { open: next, evicted: null };

    const victim = lowestOf(next, code);
    if (victim) next.delete(victim);
    return { open: next, evicted: victim };
}

export function railSlotReducer(prev: Set<string>, code: string, open: boolean): Set<string> {
    if (open === prev.has(code)) return prev;
    const next = new Set(prev);
    if (open) next.add(code);
    else next.delete(code);
    return next;
}
